/**
 * @file DPDchart.jsx
 * @description DPDchart component for displaying and managing DPD.
 * @component DPDchart
 * @exports DPDchart - Main component for displaying and managing DPD.
 */

import React, { useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// ── Date helpers ───────────────────────────────────────────────────
const parseDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
    let s = String(v).trim();
    if (!s) return null;
    // Match YYYY-MM-DD whether or not a time portion follows. ISO timestamps
    // like "2026-05-08T00:00:00.000Z" would otherwise be parsed as UTC midnight
    // and land on the previous day in negative-offset timezones.
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
        return new Date(+m[1], +m[2] - 1, +m[3], 0, 0, 0, 0);
    }
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
};

const toLocalYMD = (v) => {
    const d = parseDate(v);
    if (!d) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const prettyLabel = (ymd) => {
    const [yy, mm, dd] = ymd.split("-").map(Number);
    return new Date(yy, mm - 1, dd).toLocaleDateString(undefined, { month: "short", day: "2-digit" });
};

const toNumber = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

// ── DPD buckets — matches TrendsDashboard pastdue ranges ───────────
const DPD_BUCKETS = [
    { key: "days_1_9",     label: "1–9 Days"   },
    { key: "days_10_19",   label: "10–19 Days" },
    { key: "days_20_29",   label: "20–29 Days" },
    { key: "days_30_39",   label: "30–39 Days" },
    { key: "days_40_49",   label: "40–49 Days" },
    { key: "days_50_59",   label: "50–59 Days" },
    { key: "days_60_89",   label: "60–89 Days" },
    { key: "days_90_plus", label: "90+ Days"   },
];

// Lightest blue → blue → amber → pink progression to mirror severity.
// 8 colors for 8 buckets.
const COLORS = ["#D8EAF8", "#B5D4F4", "#7FAEDB", "#378ADD", "#185FA5", "#BA7517", "#D4537E", "#0C447C"];

const DPDStackedAreaChart = ({ rows = [], title = "DPD" }) => {
    const { labels, series } = useMemo(() => {
        const map = new Map();
        (rows || []).forEach((r) => {
            const key = toLocalYMD(r?.summary_date);
            if (!key) return;
            const cur = map.get(key) || {};
            DPD_BUCKETS.forEach(({ key: colKey }) => {
                cur[colKey] = (cur[colKey] || 0) + toNumber(r?.[colKey]);
            });
            map.set(key, cur);
        });
        const keys = [...map.keys()].sort();
        return {
            labels: keys.map(prettyLabel),
            series: DPD_BUCKETS.map(({ key, label }, i) => ({
                label,
                data: keys.map(k => map.get(k)?.[key] ?? 0),
                backgroundColor: COLORS[i % COLORS.length],
            })),
        };
    }, [rows]);

    if (!rows.length || !labels.length) return null;

    const data = {
        labels,
        datasets: series.map(s => ({
            label:           s.label,
            data:            s.data,
            backgroundColor: s.backgroundColor,
            fill:            true,
            stack:           "dpd",
            borderWidth:     0,
            pointRadius:     0,
            pointHoverRadius: 0,
            tension:         0.35,
        })),
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
            legend: {
                position: "top",
                labels: { boxWidth: 10, boxHeight: 10, font: { size: 11 } },
            },
            tooltip: { enabled: true },
        },
        elements: { line: { borderWidth: 0 }, point: { radius: 0 } },
        scales: {
            x: { grid: { display: false }, ticks: { font: { size: 10 } } },
            y: {
                stacked: true,
                beginAtZero: true,
                ticks: { precision: 0, font: { size: 10 } },
                grid: { color: "rgba(0,0,0,0.04)" },
            },
        },
    };

    return (
        <Paper
            elevation={0}
            sx={{
                border: "0.5px solid", borderColor: "divider",
                borderRadius: 2, p: "12px 14px", mb: 2,
            }}
        >
            <Typography sx={{
                fontSize: 11, fontWeight: 500, color: "text.secondary",
                textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
            }}>
                {title}
            </Typography>
            <Box sx={{ height: 260 }}>
                <Line data={data} options={options} />
            </Box>
        </Paper>
    );
};

export default DPDStackedAreaChart;